// src/features/alerts/AlertBar.jsx
// Live NWS alert banner — shows highest severity active alert for user's zone.

import { useMemo } from 'react';
import { T } from '@components/theme';
import { BottomSheet } from '@components/ui';
import { useAlerts } from '@hooks/useAlerts';
import { useUserStore } from '@store/userStore';

const SEVERITY_COLOR = {
  extreme: '#7f1d1d',
  severe:  '#92400e',
  moderate:'#1e3a5f',
  minor:   '#1a3a2a',
};

const SEVERITY_TEXT = {
  extreme: '#fecaca',
  severe:  '#fde68a',
  moderate:'#bfdbfe',
  minor:   '#bbf7d0',
};

export function AlertBar({ onTap }) {
  const { activeAlerts } = useAlerts('zones');

  const topAlert = useMemo(() => {
    if (!activeAlerts?.length) return null;
    const SEV_ORDER = { extreme: 0, severe: 1, moderate: 2, minor: 3 };
    return [...activeAlerts].sort((a, b) => (SEV_ORDER[a.severity] ?? 4) - (SEV_ORDER[b.severity] ?? 4))[0] ?? null;
  }, [activeAlerts]);

  const isSimulated = topAlert?.simulated || topAlert?.headline?.includes('🧪');
  if (!topAlert) return null;

  const bg = SEVERITY_COLOR[topAlert.severity] ?? SEVERITY_COLOR.severe;
  const fg = SEVERITY_TEXT[topAlert.severity]  ?? SEVERITY_TEXT.severe;

  return (
    <div onClick={onTap} style={{ background: bg, color: fg, padding: '8px 14px',
      display: 'flex', alignItems: 'flex-start', gap: 10, flexShrink: 0,
      cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}>
      <span style={{ fontSize: 18, flexShrink: 0 }}>⚠️</span>
      <div style={{ flex: 1 }}>
        {isSimulated && (
          <div style={{ fontSize: 8, fontWeight: 900, color: '#c4b5fd',
            textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>
            🧪 Stress test — not a real emergency
          </div>
        )}
        <div style={{ fontSize: 9, fontWeight: 900, letterSpacing: 1.5,
          textTransform: 'uppercase', opacity: 0.7, marginBottom: 1 }}>
          {topAlert.severity?.toUpperCase()} — Active Alert · Tap for details
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.4 }}>
          {topAlert.title ?? topAlert.event ?? 'NWS Alert active for your area'}
        </div>
      </div>
      <span style={{ fontSize: 18, opacity: 0.8, flexShrink: 0, marginTop: 2 }}>›</span>
    </div>
  );
}

export function AlertDetailSheet({ open, onClose }) {
  const { activeAlerts } = useAlerts('zones');

  const topAlert = useMemo(() => {
    if (!activeAlerts?.length) return null;
    const SEV_ORDER = { extreme: 0, severe: 1, moderate: 2, minor: 3 };
    return [...activeAlerts].sort((a, b) => (SEV_ORDER[a.severity] ?? 4) - (SEV_ORDER[b.severity] ?? 4))[0] ?? null;
  }, [activeAlerts]);

  if (!topAlert && !open) return null;

  const expires = topAlert?.expiresAt
    ? new Date(topAlert.expiresAt).toLocaleString([], {
        weekday: 'short', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit'
      })
    : null;
  const parishes = topAlert?.parishes?.slice(0, 6).join('; ') ?? '';
  const isSimulated = topAlert?.simulated || topAlert?.headline?.includes('🧪');

  return (
    <BottomSheet open={open} onClose={onClose} maxH="80vh">
      <div style={{ padding: '8px 16px 24px' }}>
        {isSimulated && (
          <div style={{ background: '#7c3aed', color: '#fff', fontSize: 9,
            fontWeight: 900, textAlign: 'center', padding: '4px', letterSpacing: 1,
            borderRadius: 6, marginBottom: 10 }}>
            🧪 STRESS TEST SIMULATION — NOT A REAL EMERGENCY
          </div>
        )}
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'rgba(220,38,38,0.10)', border: '1px solid rgba(220,38,38,0.3)',
          borderRadius: 8, padding: '4px 10px', marginBottom: 10 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%',
            background: '#dc2626', display: 'inline-block' }} />
          <span style={{ fontSize: 10, fontWeight: 900, color: '#dc2626', letterSpacing: 1 }}>
            {topAlert?.severity?.toUpperCase() ?? 'ACTIVE'} — NWS OFFICIAL
          </span>
        </div>

        <div style={{ fontWeight: 900, fontSize: 18, color: T.text, marginBottom: 4 }}>
          {topAlert?.title ?? topAlert?.event ?? 'NWS Alert'}
        </div>

        {parishes && (
          <div style={{ fontSize: 11, color: T.dim, marginBottom: 4 }}>{parishes}</div>
        )}
        {expires && (
          <div style={{ fontSize: 11, color: T.dim, marginBottom: 8 }}>
            Until {expires}
          </div>
        )}

        {/* All active alerts list */}
        <div style={{ marginTop: 8 }}>
          {activeAlerts.slice(0, 8).map(a => (
            <a key={a.id} href={a.url ?? 'https://www.weather.gov/lix/'}
              target="_blank" rel="noreferrer"
              style={{ display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 0', borderBottom: `1px solid ${T.bdr}`,
                textDecoration: 'none' }}>
              <span style={{ fontSize: 10, fontWeight: 800,
                color: a.severity === 'extreme' || a.severity === 'severe' ? '#dc2626' : '#f59e0b',
                background: 'rgba(220,38,38,0.08)', padding: '2px 6px',
                borderRadius: 4, flexShrink: 0 }}>
                {a.severity?.toUpperCase()}
              </span>
              <span style={{ fontSize: 12, color: T.text, fontWeight: 600,
                flex: 1, lineHeight: 1.3 }}>
                {a.title ?? a.event ?? 'Alert'}
              </span>
              <span style={{ fontSize: 11, color: T.dim, flexShrink: 0 }}>→</span>
            </a>
          ))}
        </div>
      </div>
    </BottomSheet>
  );
}
